import { useMicVAD } from '@ricky0123/vad-react'
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react'
import * as ort from 'onnxruntime-web'
import * as lamejs from '@breezystack/lamejs'
import { useUpdateTranscription } from '../../../hooks/queries/transcriptionAI'
import { developmentMode } from '../../../misc/environment.utilities'

ort.env.debug = developmentMode
ort.env.logLevel = 'error'

async function convertSileroVADtoMP3(audioFloatArray) {
  const len = audioFloatArray.length
  let i = 0
  const dataAsInt16Array = new Int16Array(len)

  while (i < len) {
    dataAsInt16Array[i] = convert(audioFloatArray[i++])
  }
  function convert(n) {
    const v = n < 0 ? n * 32768 : n * 32767 // convert in range [-32768, 32767]
    return Math.max(-32768, Math.min(32768, v)) // clamp
  }

  const samples = dataAsInt16Array
  const sampleRate = 16000
  const bitrate = 128 //kbps
  const maxSamples = 512

  const mp3Data: Uint8Array[] = []
  const mp3encoder = new lamejs.Mp3Encoder(1, sampleRate, bitrate)
  let sampleChunk
  let mp3buf
  for (let it = 0; it < samples.length; it += maxSamples) {
    sampleChunk = samples.subarray(it, it + maxSamples)
    let mp3buf = mp3encoder.encodeBuffer(sampleChunk)
    if (mp3buf.length > 0) {
      mp3Data.push(mp3buf)
    }
  }
  mp3buf = mp3encoder.flush()

  if (mp3buf.length > 0) {
    mp3Data.push(mp3buf)
  }
  return new Blob(mp3Data, { type: 'audio/mp3' })
}

export const useAiConsultationAudio = (
  start: boolean,
  transcriptionUuid: string | undefined,
  setIsUserSpeaking: Dispatch<SetStateAction<boolean>>,
  setLoadingUpdateTranscription: Dispatch<SetStateAction<boolean>>,
  selectedMicrophoneDeviceId: string | undefined,
) => {
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null)

  const { mutate: updateTranscriptionWithFile, isPending } = useUpdateTranscription()

  useEffect(() => {
    if (!selectedMicrophoneDeviceId) return

    navigator.mediaDevices
      .getUserMedia({ audio: { deviceId: { exact: selectedMicrophoneDeviceId } } })
      .then((stream) => setMediaStream(stream))
      .catch((err) => {
        console.error('media error', err)
        setMediaStream(null)
      })
  }, [selectedMicrophoneDeviceId])

  useEffect(() => {
    return () => {
      mediaStream?.getTracks().forEach((track) => track.stop())
    }
  }, [mediaStream])

  const handleSpeechStart = useCallback(() => {
    setIsUserSpeaking(true)
  }, [setIsUserSpeaking])

  const handleSpeechStop = useCallback(
    async (audio: Float32Array) => {
      setIsUserSpeaking(false)
      const mp3Blob = await convertSileroVADtoMP3(audio)

      if (transcriptionUuid) {
        updateTranscriptionWithFile({ transcriptionUuid: transcriptionUuid, file: mp3Blob })
      }
    },
    [setIsUserSpeaking, transcriptionUuid, updateTranscriptionWithFile],
  )

  const vad = useMicVAD({
    model: 'v5',
    startOnLoad: false,
    minSpeechFrames: 3,
    frameSamples: 512,
    negativeSpeechThreshold: 0.5,
    preSpeechPadFrames: 3,
    stream: mediaStream ?? undefined,
    onSpeechStart: handleSpeechStart,
    onSpeechEnd: handleSpeechStop,
  })

  // Stocke `vad` dans un ref pour éviter les dépendances inutiles
  const vadRef = useRef(vad)
  useEffect(() => {
    vadRef.current = vad // On met à jour la ref si besoin
  }, [vad])

  useEffect(() => {
    setLoadingUpdateTranscription(isPending)
  }, [isPending, setLoadingUpdateTranscription])

  useEffect(() => {
    if (vadRef.current) {
      if (start) {
        vadRef.current.start()
      } else {
        vadRef.current.pause()
      }
    }
  }, [start]) // `vadRef.current` ne change pas et ne crée pas de boucle infinie
}
